import React, { useState, useRef } from "react";
import { uploadTradeData, auth } from "../firebase";
import {
  FileInput,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Accordion,
  AccordionContent,
  AccordionPanel,
  AccordionTitle,
  List,
  Alert,
} from "flowbite-react";
import { handlePdfExtraction } from "./DataUploader/PdfUploader";
import { handleCsvCaptailExtraction } from "./DataUploader/CsvCapitalUploader";
import { handleHtmlExtraction } from "./DataUploader/HtmlUploader";
import { handleEmailExtraction } from "./DataUploader/EmailUploader";
import { handleTlgExtraction } from "./DataUploader/TlgIBKRUploader";
import { HiExclamationCircle, HiCheckCircle, HiThumbUp } from "react-icons/hi";

const desiredHeader = [
  "Id",
  "Balance",
  "Amount",
  "Currency",
  "Type",
  "Status",
  "Modified",
  "Trade Id",
  "Instrument Symbol",
  "Instrument Name",
  "Commission",
  "Account type",
];

const DataUploader = ({ onUploadSuccess }) => {
  const [data, setData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      let extractedData;
      if (file.name.endsWith(".pdf")) {
        extractedData = await handlePdfExtraction(file);
      } else if (file.name.endsWith(".csv")) {
        extractedData = await handleCsvCaptailExtraction(file);
      } else if (file.name.endsWith(".html")) {
        extractedData = await handleHtmlExtraction(file);
      } else if (file.name.endsWith(".eml")) {
        extractedData = await handleEmailExtraction(file);
      } else if (file.name.endsWith(".tlg")) {
        extractedData = await handleTlgExtraction(file);
      } else {
        throw new Error("Unsupported file type");
      }

      setData(extractedData);
    } catch (error) {
      console.error("Error processing file:", error);
      setError(`Error processing file: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    const user = auth.currentUser;

    if (data.length > 0 && user) {
      setIsUploading(true);
      setError(null);
      try {
        const result = await uploadTradeData(user.uid, data);
        if (typeof onUploadSuccess === "function") {
          onUploadSuccess();
        }
        setResult(`Data uploaded successfully! Added: ${result.added}, Skipped: ${result.skipped}`);
        setData([]);
      } catch (err) {
        console.error("Error uploading data:", err);
        setError(`Error uploading data: ${err.message}`);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } else if (!user) {
      setError("Please sign in to upload data.");
    } else {
      setError("No data to upload.");
    }
  };

  return (
    <main className="fh-full overflow-y-auto bg-slate-100 dark:bg-slate-900 text-slate-400">
      <div className="h-screen p-4 flex-grow">
        <h2 className="text-2xl font-bold mb-4">Data Uploader</h2>

        <div className="flex w-full items-center justify-center pt-4">
          <Label
            htmlFor="dropzone-file"
            className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-700 dark:hover:border-slate-500 dark:hover:bg-slate-600"
          >
            <div className="flex flex-col items-center justify-center pb-6 pt-5">
              <svg
                className="mb-4 h-8 w-8 text-slate-500 dark:text-slate-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">PDF, CSV, EML or HTML files</p>
            </div>
            <FileInput
              id="dropzone-file"
              className="hidden"
              accept=".pdf,.csv,.html,.eml"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </Label>
        </div>

        {error && (
          <Alert color="failure" icon={HiExclamationCircle} className="mt-4">
            <span className="font-semibold">Error!</span> {error}
          </Alert>
        )}
        {result && (
          <Alert color="success" icon={HiCheckCircle} className="mt-4">
            <span className="font-semibold">Success!</span> {result}
          </Alert>
        )}


        {data.length > 0 && (
          <>
            <Alert color="info" icon={HiThumbUp} className="mt-4">
              <span className="font-semibold">Extracted!</span> Below is a preview of your trades. Click the 'Upload Converted Data' to add to your account and click Dashboard to view your trades
            </Alert>
            <div className="mt-4">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-slate-400"
              >
                {isUploading ? "Uploading..." : "Upload Converted Data"}
              </button>

              <div className="mt-4 overflow-x-auto">
                {data.length > 10 && (
                  <p className="mt-2 text-sm text-slate-500 pb-2">Showing first 10 rows of {data.length} total rows</p>
                )}
                <Table hoverable className="bg-transparent">
                  <TableHead>
                    {desiredHeader.map((header, index) => (
                      <TableHeadCell key={index}>{header}</TableHeadCell>
                    ))}
                  </TableHead>
                  <TableBody className="divide-y">
                    {data.slice(0, 10).map((row, rowIndex) => (
                      <TableRow key={rowIndex} className="dark:border-slate-700 dark:bg-slate-800">
                        {desiredHeader.map((header, cellIndex) => (
                          <TableCell key={cellIndex}>{row[header]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}

        <Accordion collapseAll className="mt-10">
          <AccordionPanel>
            <AccordionTitle>CSV - Capital.com</AccordionTitle>
            <AccordionContent>
              <p>
                You can easily find it on the Capital.com platform. Just follow several simple steps to get your trade
                confirmation:
              </p>
              <List ordered className="py-4">
                <List.Item>After you have opened a trade, go to the “Reports” section&nbsp;</List.Item>
                <List.Item>Select the “Activity” tab</List.Item>
                <List.Item>Press the “Send report” button for the selected date</List.Item>
              </List>
              <img alt="" src="/images/captitalreport.png" className="max-md:w-full w-2/3" />
            </AccordionContent>
          </AccordionPanel>
          <AccordionPanel>
            <AccordionTitle>PDF - Trading212</AccordionTitle>
            <AccordionContent>
              <p>The PDF has been sent to your T212 account email address the following morning. </p>
              <List ordered className="py-4">
                <List.Item>A preview of the data will be provided here for you to confirm its accuracy.</List.Item>
              </List>
              <img alt="" src="/images/t212PDF.png" className="max-md:w-full w-2/3" />
            </AccordionContent>
          </AccordionPanel>
          <AccordionPanel>
            <AccordionTitle>EMAIL - Trading212</AccordionTitle>
            <AccordionContent>
              <p>A monthly email summarizing your activity will be sent at the beginning of each month.</p>
              <List ordered className="py-4">
                <List.Item>Save the email as .eml file</List.Item>
                <List.Item>Use the box above to upload the .eml file</List.Item>
              </List>
              <img alt="" src="/images/t21dailyemail.png" className="max-md:w-full w-2/3" />
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
    </main>
  );
};

export default DataUploader;
